import React, { useContext, useEffect, useState, useRef } from "react"
import { useSelector } from "react-redux"
import { Alert } from "reactstrap"
import { Trans } from "@lingui/react"
import PropTypes from "prop-types"
import { NetworkStatusContext } from "../../providers/NetworkStatusProvider"
import { useLiveQuery } from "dexie-react-hooks"
import { db } from "../../view/shared/utilities/cache"
import styles from "./NetworkStatusBanner.module.css"

const StatusBanner = ({ extraStyles }) => {
  let timeoutRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false)
  const { queue, user } = useSelector(({ user, queue }) => ({
    queue,
    user,
  }))
  const { isOnline } = useContext(NetworkStatusContext)
  const pendingRequests = useLiveQuery(
    () => {
      try {
        if (!user?.id) return []
        return db.offlineRequests
          .filter(
            ({ userId, status }) => userId === user.id && status?.toLocaleLowerCase() === "processing"
          )
          .toArray()
      } catch (error) {
        console.log(`Error: ${error}`)
        return []
      }
    },
    [user?.id]
  )

  useEffect(() => {
    // Determine the new value without immediately setting it
    const shouldBeUploading = isOnline &&
      queue.category &&
      queue.category !== 'groups' &&
      (queue.isUploading || pendingRequests?.length > 0);

    // If the new value is true, set the state immediately to be responsive
    if (shouldBeUploading) {
      // Clear any existing timeout to prevent a delayed "false"
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsUploading(true);
    } else {
      // If the new value is false, set a timeout
      // This creates the buffer you're looking for
      if (!timeoutRef.current) {
        timeoutRef = setTimeout(() => {
          setIsUploading(false);
        }, 5000); // 5 seconds buffer, adjust as needed
      }
    }

    // Cleanup function to clear the timeout if the component unmounts
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [pendingRequests, isOnline, queue.isUploading, queue.category]);

  return (
    <div className={styles.bannerContainer} style={extraStyles}>
      {!isOnline && (
        <Alert style={{ padding: "2px", margin: 2 }} className={styles.alert} color="danger" fade={true} timeout={1000}>
          <Trans id="no.internet.connection" />
        </Alert>
      )}
      {isUploading && (
        <Alert style={{ padding: "2px", margin: 2 }} className={styles.alert} color="info" fade={true} timeout={1000}>
          <Trans id="uploading.transactions" />
        </Alert>
      )}
    </div>
  )
}

StatusBanner.propTypes = {
  extraStyles: PropTypes.object,
}

export default StatusBanner
